import {
  AlertTriangle, ArrowRight, Calendar, ChevronDown, ChevronUp, Code, ExternalLink, Eye, MapPin,
} from 'react-feather';
import { ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetEventLayoutQuery as GetEventLayout } from '__generated__/graphql';

import { getEmbedScript, resizedImageUrl } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import ActivateEventMutation from './ActivateEventMutation';
import CharityPicker from '../../Fundraising/CharityPicker';
import CopyLink, { CopyLinkInput } from '../../../common/Common/CopyLink';
import DateRange from '../../../common/Common/DateRange';
import DeactivateEventMutation from './DeactivateEventMutation';
import GetEventLayoutQuery from './GetEventLayoutQuery';
import OptionsDropdown, { ExternalOption, Option } from '../../../common/Common/OptionsDropdown';
import UI from '../../../common/UI';
import config from '../../config';
import useAtletaEmbed, { AtletaEmbed } from '../../../common/useAtletaEmbed';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useUrlState from '../../../common/useUrlState';

type Project = GetEventLayout['project'];
type Event = Project['event'];

export interface EventLayoutProps {
  eventId: string;
  children: ReactNode;
}

const EventLayout = ({ eventId, children }: EventLayoutProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const device = useMediaDevice();
  const project = useProject();
  const notifier = useNotifier();

  const { data } = useQuery(GetEventLayoutQuery, {
    variables: {
      project_id: project.id,
      event_id: eventId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const event = data?.project.event;

  const [activateEvent] = useMutation(ActivateEventMutation, {
    variables: {
      id: eventId,
    },
    optimisticResponse: {
      activateEvent: {
        id: eventId,
        active: true,
        __typename: 'Event',
      },
    },
    onCompleted: () => notifier.success(t('notifications.event_activated')),
  });

  const [deactivateEvent] = useMutation(DeactivateEventMutation, {
    variables: {
      id: eventId,
    },
    optimisticResponse: {
      deactivateEvent: {
        id: eventId,
        active: false,
        __typename: 'Event',
      },
    },
    onCompleted: () => notifier.success(t('notifications.event_deactivated')),
  });

  const toggleStatus = () => {
    if (event.active) {
      deactivateEvent();
    } else {
      activateEvent();
    }
  };

  const embed = useAtletaEmbed();

  const [openEmbedModal, closeEmbedModal] = useModal(() => (
    <EmbedModal event={event} embed={embed} close={closeEmbedModal} />
  ), [event]);

  const [urlState, setUrlState] = useUrlState({
    parse: (params, { string }) => ({ modal: string(params.modal) }),
  });
  useEffect(() => {
    if (event && urlState.modal === 'embed') {
      openEmbedModal();
      setUrlState((params) => ({ ...params, modal: undefined }), true);
    }
  });

  return (
    <>
      {event && (
        <TestModeAlert project={data.project} event={event} />
      )}
      <UI.Hero>
        <UI.Section>
          <UI.GridContainer
            sc={{ columns: ['1fr', '4fr 2fr'], alignVertical: 'center' }}
            style={{ minHeight: device.isSmall ? 171 : 62 }}
          >
            {event && (
              <>
                <UI.GridContainer sc={{ columns: ['40px 1fr', '60px 1fr'], alignVertical: 'center', gutter: 0.5 }}>
                  <UI.Div>
                    <UI.ProfilePic
                      sc={{ background: event.logo ? 'white' : undefined, size: [40, 60] }}
                    >
                      {event.logo && (
                        <img src={resizedImageUrl(event.logo.url, 300, 'webp')} alt={event.title} />
                      )}
                      {!event.logo && (
                        <UI.Span><UI.Span>{event.title.charAt(0)}</UI.Span></UI.Span>
                      )}
                    </UI.ProfilePic>
                  </UI.Div>
                  {/* Need overflow hidden for truncate */}
                  <UI.Div style={{ overflow: 'hidden' }}>
                    <UI.H1>
                      <UI.Truncate>
                        {event.title}
                      </UI.Truncate>
                    </UI.H1>

                    {!device.isSmall && <HeaderSubTitle event={event} />}
                  </UI.Div>
                </UI.GridContainer>

                {device.isSmall && (
                  <UI.Div>
                    <HeaderSubTitle event={event} />
                  </UI.Div>
                )}

                {device.isSmall && <UI.HR />}

                <UI.Div sc={{
                  flex: device.isSmall && { alignItems: 'center' },
                  textAlign: ['left', 'right'],
                  mt: [0, 1],
                }}
                >
                  <UI.Div>
                    <UI.Legend sc={{ mr: 2, inlineBlock: true }}>
                      {t(event.active ? 'live' : 'offline')}
                    </UI.Legend>
                    <UI.Toggle
                      checked={event.active}
                      onChange={() => toggleStatus()}
                      aria-label={event.active ? t('deactivate_event') : t('activate_event')}
                    />
                  </UI.Div>
                  <UI.Div sc={{ ml: 2, strong: true }}>
                    <a href={`${event.checkout_url}?locale=${locale}`} rel="noopener noreferrer">
                      {t('view_checkout')}
                    </a>

                    <OptionsDropdown sc={{ ml: 1 }}>
                      <Option
                        icon={<Code />}
                        label={t('embed_and_links')}
                        onClick={() => openEmbedModal()}
                      />
                      <ExternalOption
                        icon={<ExternalLink />}
                        label={t('visit_page')}
                        href={event.checkout_url}
                        data-atleta-embed="false"
                      />
                    </OptionsDropdown>
                  </UI.Div>
                </UI.Div>
              </>
            )}
          </UI.GridContainer>
        </UI.Section>

        <UI.Container sc={{ px: [0, 4] }}>
          <UI.Tabs>
            <UI.Tab to={`/${project.id}/events/${eventId}/tickets`}>
              {t('tickets')}
            </UI.Tab>
            <UI.Tab to={`/${project.id}/events/${eventId}/products`}>
              {t('products')}
            </UI.Tab>
            <UI.Tab to={`/${project.id}/events/${eventId}/invitation-codes`}>
              {t('invitation_codes')}
            </UI.Tab>
            <UI.Tab to={`/${project.id}/events/${eventId}/discounts`}>
              {t('discounts')}
            </UI.Tab>
            <UI.Tab to={`/${project.id}/events/${eventId}/coupons`}>
              {t('coupons')}
            </UI.Tab>
            <UI.Tab to={`/${project.id}/events/${eventId}/fields`}>
              {t('fields')}
            </UI.Tab>
            <UI.Tab to={`/${project.id}/events/${eventId}/resale`}>
              {t('resale')}
            </UI.Tab>
            <UI.Tab to={`/${project.id}/events/${eventId}/fundraising`}>
              {t('fundraising')}
            </UI.Tab>
            <UI.Tab to={`/${project.id}/events/${eventId}/settings`}>
              {t('settings')}
            </UI.Tab>
          </UI.Tabs>
        </UI.Container>
      </UI.Hero>

      <UI.Div id="EventTab">
        {children}
      </UI.Div>
    </>
  );
};

interface TestModeAlertProps {
  project: Project;
  event: Event;
}

const TestModeAlert = ({ project, event }: TestModeAlertProps) => {
  const { t } = useTranslation();

  const isVerified = project.verified && (event.payments_enabled || !event.requires_payments);

  if (isVerified || event.tickets_count === 0) {
    return <></>;
  }

  return (
    <UI.Section sc={{ background: 'info', color: 'secondary.800', pt: 3, pb: 3 }}>
      <UI.FlexContainer
        sc={{
          justifyContent: [undefined, 'space-between'], alignItems: [undefined, 'center'], direction: ['column', 'row'],
        }}
      >
        <UI.Div>
          <UI.Label sc={{ small: true, mr: 2 }}>
            <UI.Icon>
              <AlertTriangle />
            </UI.Icon>
            {' '}
            {t('test_mode')}
          </UI.Label>
          <UI.Span sc={{ fontWeight: 500 }}>
            {!event.requires_payments && t('project_unverified_warning')}
            {event.requires_payments && project.psp_connected && t('cannot_receive_payments_awaiting_verification')}
            {event.requires_payments && !project.psp_connected && t('cannot_receive_payments_no_payment_provider')}
          </UI.Span>
        </UI.Div>
        <UI.Div sc={{ textAlign: ['left', 'right'], mt: [2, 0] }}>
          {!event.requires_payments && (
            <UI.AButton
              href="/contact"
              sc={{ brand: 'secondary.800', size: 'sm', outline: true }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('get_verified')}
              {' '}
              <UI.Icon>
                <ArrowRight />
              </UI.Icon>
            </UI.AButton>
          )}
          {event.requires_payments && (
            <>
              {project.psp_connected && (
                <UI.NavButton
                  to={`/${project.id}/project/payments`}
                  sc={{ brand: 'secondary.800', size: 'sm', outline: true }}
                >
                  {t('view_status')}
                </UI.NavButton>
              )}
              {!project.psp_connected && (
                <UI.NavButton
                  to={`/${project.id}/project/payments`}
                  sc={{ brand: 'secondary.800', size: 'sm', outline: true }}
                >
                  {t('set_up_payments')}
                  {' '}
                  <UI.Icon>
                    <ArrowRight />
                  </UI.Icon>
                </UI.NavButton>
              )}
            </>
          )}
        </UI.Div>
      </UI.FlexContainer>
    </UI.Section>
  );
};

interface HeaderSubTitleProps {
  event: Event;
}

const HeaderSubTitle = ({ event }: HeaderSubTitleProps) => (
  <UI.FlexContainer sc={{ color: 'gray.500', fontWeight: 500 }}>
    <UI.Span sc={{ mr: 2 }}>
      <UI.Icon sc={{ mr: 1 }}>
        <Calendar />
      </UI.Icon>
      <DateRange start={event.start_date} end={event.end_date} />
    </UI.Span>

    {(event.location.id || event.location.description) && (
      <UI.Span>
        <UI.Icon sc={{ mr: 1 }}>
          <MapPin />
        </UI.Icon>
        <UI.Span>
          {event.location.id ? event.location.description.split(',')[0] : event.location.description}
        </UI.Span>
      </UI.Span>
    )}
  </UI.FlexContainer>
);

interface EmbedModalProps {
  event: Event;
  embed: AtletaEmbed;
  close: () => void;
}

const EmbedModal = ({ event, embed, close }: EmbedModalProps) => {
  const { t } = useTranslation();

  const [enableButton, setEnableButton] = useState(false);

  const { sortedLocales } = useLocale();

  useEffect(() => {
    embed.options.eventId = enableButton ? event.id : null;
    embed.render();

    return () => {
      embed.options.eventId = null;
      embed.render();
    };
  }, [embed, enableButton, event.id]);

  const script = getEmbedScript(event?.id, enableButton);

  const form = useForm({
    values: {
      ticket_category_ids: [],
      locale: '',
      charity: null,
      coupon: '',
      invitation_code: '',
    },
  });

  const [showLinkOptions, setShowLinkOptions] = useState(false);

  const checkoutUrl = new URL(event.checkout_url);
  if (form.values.locale) {
    checkoutUrl.searchParams.append('locale', form.values.locale);
  }
  if (form.values.ticket_category_ids && form.values.ticket_category_ids.length > 0) {
    form.values.ticket_category_ids.forEach((ticketCategoryId) => {
      checkoutUrl.searchParams.append('ticketCategoryIds', ticketCategoryId);
    });
  }
  if (form.values.charity) {
    checkoutUrl.searchParams.append('charityId', form.values.charity.id);
  }
  if (form.values.coupon) {
    checkoutUrl.searchParams.append('coupon', form.values.coupon);
  }
  if (form.values.invitation_code) {
    checkoutUrl.searchParams.append('invitationCode', form.values.invitation_code);
  }
  const augmentedCheckoutUrl = checkoutUrl.toString().replace('ticketCategoryIds', 'ticketCategoryIds[]');

  const dashboardUrl = `${config.baseUrl}/me`;

  return (
    <UI.Modal maxWidth={800} close={close}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('embed_and_links')}
          </UI.H4>
          <UI.Paragraph>
            {t('embed_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.HR />

        <UI.Div style={{ minWidth: 0 }}>
          <UI.FlexContainer sc={{ mb: 1, justifyContent: 'space-between', alignItems: 'center' }}>
            <UI.Legend>
              {t('html_snippet')}
            </UI.Legend>
            <CopyLink value={script}>
              {t('copy_script')}
            </CopyLink>
          </UI.FlexContainer>
          <UI.Div sc={{ background: 'gray.100', padding: 2, mb: 2 }}>
            <UI.Pre>
              {script}
            </UI.Pre>
          </UI.Div>
          <UI.Checkbox
            checked={enableButton}
            onChange={() => setEnableButton((enable) => !enable)}
            sc={{ inline: true, mr: 3 }}
          >
            {t('enable_widget_button')}
          </UI.Checkbox>
        </UI.Div>

        <UI.HR />

        <UI.InputGroup>
          <UI.FlexContainer sc={{ mb: 1, justifyContent: 'space-between' }}>
            <UI.InputLabel htmlFor="CheckoutLink">
              {t('checkout_link')}
            </UI.InputLabel>
            <UI.Span sc={{ mt: 0 }}>
              <UI.A href={checkoutUrl} sc={{ mr: 3 }}>
                <UI.Icon>
                  <Eye />
                </UI.Icon>
                {' '}
                {t('preview')}
              </UI.A>
              <UI.A
                href={augmentedCheckoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-atleta-embed="false"
              >
                <UI.Icon>
                  <ExternalLink />
                </UI.Icon>
                {' '}
                {t('open_in_new_tab')}
              </UI.A>
            </UI.Span>
          </UI.FlexContainer>
          <CopyLinkInput value={augmentedCheckoutUrl} id="CheckoutLink" />
        </UI.InputGroup>

        <UI.Div>
          <UI.A onClick={() => setShowLinkOptions((show) => !show)} role="button">
            <UI.Icon>
              {showLinkOptions ? <ChevronUp /> : <ChevronDown />}
            </UI.Icon>
            {' '}
            {t('show_link_options')}
          </UI.A>

          <UI.AnimateHeight isVisible={showLinkOptions}>
            <UI.FormGrid sc={{ pt: [3, 4] }}>
              <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
                <UI.InputGroup>
                  <UI.InputLabel htmlFor="Locale">
                    {t('locale')}
                  </UI.InputLabel>
                  <UI.Select
                    {...form.select('locale')}
                    sc={{ placeholder: !form.values.locale }}
                    id="Locale"
                  >
                    <option value="">{t('determined_by_browser')}</option>
                    {sortedLocales.map((locale) => (
                      <option value={locale} key={locale}>
                        {t(`common:locales.${locale}`)}
                      </option>
                    ))}
                  </UI.Select>
                </UI.InputGroup>
                <UI.InputGroup>
                  <UI.InputLabel>
                    {t('preselected_charity')}
                  </UI.InputLabel>
                  <CharityPicker charity={form.values.charity} onChange={(charity) => form.set('charity', charity)} />
                </UI.InputGroup>
              </UI.FormGrid>

              <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
                <UI.InputGroup>
                  <UI.InputLabel htmlFor="Coupon">
                    {t('preselected_coupon_code')}
                  </UI.InputLabel>
                  <UI.DebouncedInput {...form.text('coupon')} id="Coupon" />
                </UI.InputGroup>
                <UI.InputGroup>
                  <UI.InputLabel htmlFor="InvitationCode">
                    {t('preselected_invitation_code')}
                  </UI.InputLabel>
                  <UI.DebouncedInput {...form.text('invitation_code')} id="InvitationCode" />
                </UI.InputGroup>
              </UI.FormGrid>

              <UI.InputGroup>
                <UI.InputLabel htmlFor="Ticketcategory">
                  {t('ticket_category')}
                </UI.InputLabel>
                <UI.Checkbox
                  checked={form.values.ticket_category_ids.length === 0}
                  disabled={form.values.ticket_category_ids.length === 0}
                  onChange={() => form.set('ticket_category_ids', [])}
                >
                  {t('all_ticket_categories')}
                </UI.Checkbox>
                {event.ticket_categories.map((ticketCategory) => (
                  <UI.Checkbox
                    {...form.checklist('ticket_category_ids', ticketCategory.id)}
                    key={ticketCategory.id}
                  >
                    {ticketCategory.title}
                  </UI.Checkbox>
                ))}
              </UI.InputGroup>
            </UI.FormGrid>
          </UI.AnimateHeight>
        </UI.Div>

        <UI.HR />

        <UI.InputGroup>
          <UI.InputLabel htmlFor="ResaleLink">
            {t('resale_link')}
          </UI.InputLabel>
          <CopyLinkInput value={event.resale_url} id="ResaleLink" />
        </UI.InputGroup>

        <UI.InputGroup>
          <UI.InputLabel htmlFor="InterestListLink">
            {t('interest_list_link')}
          </UI.InputLabel>
          <CopyLinkInput value={event.interest_list_url} id="InterestListLink" />
        </UI.InputGroup>

        <UI.InputGroup>
          <UI.InputLabel htmlFor="DashboardLink">
            {t('dashboard_link')}
          </UI.InputLabel>
          <CopyLinkInput value={dashboardUrl} id="DashboardLink" />
        </UI.InputGroup>
      </UI.FormGrid>
    </UI.Modal>
  );
};

export default EventLayout;
